import { useState } from 'react'

import {
  ReconciliationInvoicesDataTableDocument,
  ReconciliationTransactionDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  BulkManageBankingReconciliationDocument,
  BulkManageBankingReconciliationReconciliationsInput,
  CreateReconciliationWithoutDocumentDocument,
} from 'generated/resources/graphql'
import { sum } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { Reconcilable } from '../components/NewReconciliationForm/SplitReconciledAmountsModal/types'

export enum ReconciliationStatusEnum {
  None = 'none',
  Full = 'full',
  Partial = 'partial',
}

export enum ReconciliationType {
  Transaction = 'Transaction',
  Invoice = 'Invoice',
}

export type ReconciliationSource = {
  id: number
  description: string
  secondaryDescription?: string
  amountCentsToReconcile: number
  type: 'debit' | 'credit'
  currency: string
  date: string
  locale: string
  reconciliationStatus: ReconciliationStatusEnum
  reconciliationType: ReconciliationType
}

export interface ReconciliationItem {
  id: number // represents the id of a transaction or an invoice
  reconciliationId?: number
  description: string
  amountCentsToReconcile: number
  type: 'debit' | 'credit'
  currency: string
  date: string
  locale: string
  secondaryDescription?: string
}

interface Data {
  transactionId: number
  transactionAmountCents: number
  transactionCurrency: string
  reconcilableId: number
  reconcilableAmountCents: number
  reconcilableCurrency: string
}

interface Props {
  source: ReconciliationSource | undefined
  sourceReconciliations: ReconciliationItem[]
  onTransactionFullyReconciled?: () => void
  setShowSplitAmountsModal: (value: boolean) => void
  setShowPartialPendingInvoiceModal: (value: boolean) => void
  setShowPartialPendingTransactionModal: (value: boolean) => void
  setShowNoDocumentModal?: (value: boolean) => void
}

export const useReconciliationForm = ({
  source,
  sourceReconciliations,
  onTransactionFullyReconciled,
  setShowSplitAmountsModal,
  setShowPartialPendingInvoiceModal,
  setShowPartialPendingTransactionModal,
  setShowNoDocumentModal,
}: Props) => {
  const { company } = useCurrentSession()
  const [dirtyForm, setDirtyForm] = useState<boolean>(false)

  const isSourceTypeTransaction = (): boolean => {
    if (!source) {
      return false
    }
    return source.reconciliationType === ReconciliationType.Transaction
  }

  const transactionId = (item: ReconciliationItem): number => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return isSourceTypeTransaction() ? source!.id : item.id
  }
  const reconcilableId = (item: ReconciliationItem): number => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return isSourceTypeTransaction() ? item.id : source!.id
  }

  const transactionCurrency = (item: ReconciliationItem): string => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return isSourceTypeTransaction() ? source!.currency : item.currency
  }

  const reconcilableCurrency = (item: ReconciliationItem): string => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return isSourceTypeTransaction() ? item.currency : source!.currency
  }

  const [selectedReconciliations, setSelectedReconciliations] = useState<ReconciliationItem[]>([])
  const [reconciliationIdsToRemove, setReconciliationIdsToRemove] = useState<number[]>([])
  const newReconciliations = selectedReconciliations.filter(
    (reconciliation) => reconciliation.reconciliationId === undefined
  )

  useUnsafeEffect(() => {
    setSelectedReconciliations(sourceReconciliations)
    setDirtyForm(false)
  }, [sourceReconciliations])

  const savedReconciliations = selectedReconciliations.filter(
    (reconciliation) => reconciliation.reconciliationId != undefined
  )

  const [reconciliationValues, setReconciliationValues] = useState<Data[]>([])

  const [bulkManageBankingReconciliations] = useMutation(BulkManageBankingReconciliationDocument, {
    extractorFn: (mutationData) => mutationData.bankingMutations.bulkManageBankingReconciliation,
    refetchQueries: isSourceTypeTransaction()
      ? [ReconciliationInvoicesDataTableDocument]
      : [ReconciliationTransactionDataTableDocument],
  })

  const [createReconciliationWithoutDocument] = useMutation(
    CreateReconciliationWithoutDocumentDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.bankingMutations.createWithoutDocumentBankingReconciliation,
      refetchQueries: [ReconciliationInvoicesDataTableDocument],
    }
  )

  const notRemovedReconciliationsAmount =
    savedReconciliations.reduce(
      (acc, r) =>
        r.reconciliationId &&
        !reconciliationIdsToRemove.includes(r.reconciliationId) &&
        !!r.amountCentsToReconcile
          ? (acc += r.amountCentsToReconcile)
          : acc,
      0
    ) ?? 0

  const initialAmountPendingToBeReconciled =
    (source?.amountCentsToReconcile || 0) - notRemovedReconciliationsAmount

  const amountCentsToBeReconciled = sum(
    newReconciliations.map((reconciliation) => reconciliation.amountCentsToReconcile)
  )

  // We want the first reconciliation that hasn't been created yet, meaning that
  // the user has added it.
  const firstNewReconciliation = newReconciliations[0]

  const removeReconciliation = (reconciliation: ReconciliationItem) => {
    setDirtyForm(true)
    if (reconciliation.reconciliationId) {
      setReconciliationIdsToRemove([...reconciliationIdsToRemove, reconciliation.reconciliationId])
    }
    const reconciliationsWithoutSelected = selectedReconciliations.filter(
      (selectedReconciliation) => selectedReconciliation.id != reconciliation.id
    )
    setSelectedReconciliations(reconciliationsWithoutSelected)
    return reconciliationsWithoutSelected
  }

  const addReconciliation = (reconciliation: ReconciliationItem) => {
    if (reconciliation.id && !isSelectedNewReconciliation(reconciliation.id)) {
      setDirtyForm(true)
      const existingReconciliation = selectedReconciliations.find(
        (r) => r.id === reconciliation.id && r.reconciliationId != undefined
      )
      if (existingReconciliation) {
        const reconciliationsWithoutExisting = removeReconciliation(existingReconciliation)
        const newReconciliation = {
          ...reconciliation,
          amountCentsToReconcile:
            existingReconciliation.amountCentsToReconcile + reconciliation.amountCentsToReconcile,
        }
        setSelectedReconciliations([...reconciliationsWithoutExisting, newReconciliation])
      } else {
        setSelectedReconciliations([...selectedReconciliations, { ...reconciliation }])
      }
    }
  }

  const isSelectedNewReconciliation = (id: number) => {
    return newReconciliations.some((selectedReconciliation) => selectedReconciliation.id === id)
  }

  const createReconciliationData = (
    reconciliationItem: ReconciliationItem,
    amountCentsToReconcile: number
  ) => {
    return {
      transactionId: transactionId(reconciliationItem),
      transactionCurrency: transactionCurrency(reconciliationItem),
      transactionAmountCents: amountCentsToReconcile,
      reconcilableId: reconcilableId(reconciliationItem),
      reconcilableCurrency: reconcilableCurrency(reconciliationItem),
      reconcilableAmountCents: amountCentsToReconcile,
    }
  }

  const handleReconcileClick = () => {
    if (!source) {
      const message = isSourceTypeTransaction()
        ? i18n.t('banking.reconciliations.submit_messages.no_transaction_selected')
        : i18n.t('banking.reconciliations.submit_messages.no_invoice_selected')

      app.setFlash({
        type: 'wrong',
        message,
      })

      return
    }

    if (selectedReconciliations.some((reconciliation) => reconciliation.type === source.type)) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'banking.reconciliations.submit_messages.select_same_debit_and_credit_amounts'
        ),
      })
      return
    }

    if (initialAmountPendingToBeReconciled === 0 && !reconciliationIdsToRemove.length) {
      const message = isSourceTypeTransaction()
        ? i18n.t('banking.reconciliations.submit_messages.no_pending_amount')
        : i18n.t('banking.reconciliations.submit_messages.no_pending_amount_invoice')

      app.setFlash({
        type: 'wrong',
        message: message,
      })

      return
    }

    if (!selectedReconciliations.length && !reconciliationIdsToRemove.length) {
      const message = isSourceTypeTransaction()
        ? i18n.t('banking.reconciliations.submit_messages.no_invoices_selected')
        : i18n.t('banking.reconciliations.submit_messages.no_transactions_selected')

      app.setFlash({
        type: 'wrong',
        message: message,
      })

      return
    }

    if (selectedReconciliations.length === 0 && reconciliationIdsToRemove.length > 0) {
      submitReconciliation([])
      return
    }

    if (initialAmountPendingToBeReconciled === amountCentsToBeReconciled) {
      // Both the transaction and the invoices will be fully reconciled.
      const reconciliationData = newReconciliations.map((reconciliation) => {
        return createReconciliationData(reconciliation, reconciliation.amountCentsToReconcile)
      })
      submitReconciliation(reconciliationData)
    } else if (initialAmountPendingToBeReconciled > amountCentsToBeReconciled) {
      // The transaction won't be fully reconciled, but all of the invoices will.
      const reconciliatonData = newReconciliations.map((reconciliation) => {
        return createReconciliationData(reconciliation, reconciliation.amountCentsToReconcile)
      })
      setReconciliationValues(reconciliatonData)
      setShowPartialPendingTransactionModal(true)
    } else if (initialAmountPendingToBeReconciled < amountCentsToBeReconciled) {
      // The transaction will be fully reconciled, but not all of the invoices will.
      if (newReconciliations.length > 1) {
        // We're adding many reconciliations, so the user has to split the transaction amount between them.
        setReconciliationValues([])
        setShowSplitAmountsModal(true)
      } else {
        // We're adding only one reconciliation, so we show the partial pending invoice modal.
        const reconciliatonData = newReconciliations.map((reconciliation) => {
          return createReconciliationData(reconciliation, initialAmountPendingToBeReconciled)
        })
        setReconciliationValues(reconciliatonData)
        setShowPartialPendingInvoiceModal(true)
      }
    }
  }

  const handleNoDocumentReconcileClick = () => {
    if (!source) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.submit_messages.no_transaction_selected'),
      })
      return
    }

    if (selectedReconciliations.length) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.submit_messages.invoices_selected'),
      })
      return
    }
    if (setShowNoDocumentModal) {
      setShowNoDocumentModal(true)
    }
  }

  const handleSubmitSplitAmountsModal = (items: Reconcilable[]) => {
    if (source) {
      // This only works if currencies are the same
      const reconciliatonData = items.map((item) => ({
        transactionId: isSourceTypeTransaction() ? source.id : item.id,
        transactionCurrency: isSourceTypeTransaction() ? source.currency : item.currency,
        transactionAmountCents: item.updatedAmountCents,
        reconcilableId: isSourceTypeTransaction() ? item.id : source.id,
        reconcilableCurrency: isSourceTypeTransaction() ? item.currency : source.currency,
        reconcilableAmountCents: item.updatedAmountCents,
      }))
      setReconciliationValues(reconciliatonData)
      submitReconciliation(reconciliatonData)
    }
  }

  const handleNoDocumentSubmitModal = async () => {
    const result = await createReconciliationWithoutDocument({
      variables: {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        transactionId: source!.id,
        companyId: company.id,
      },
    })
    app.setProgress(null)
    if (setShowNoDocumentModal) {
      setShowNoDocumentModal(false)
    }

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.submit_messages.creating_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.reconciliations.submit_messages.creating_success'),
      })
      setReconciliationValues([])
      if (onTransactionFullyReconciled) {
        onTransactionFullyReconciled()
      }
    }
  }

  const handleSubmitPartialModal = () => {
    submitReconciliation(reconciliationValues)
  }

  const submitReconciliation = async (values: Data[]) => {
    app.setProgress({
      message: i18n.t('banking.reconciliations.submit_messages.creating_reconciliation'),
    })

    const payload: BulkManageBankingReconciliationReconciliationsInput[] = values.map((item) => {
      return {
        reconcilable: {
          id: item.reconcilableId,
          type: 'invoice',
        },
        reconcilableCurrency: item.reconcilableCurrency,
        reconcilableReconciledAmountCents: item.reconcilableAmountCents,
        transactionCurrency: item.transactionCurrency,
        transactionId: item.transactionId,
        transactionReconciledAmountCents: item.transactionAmountCents,
      }
    })

    const sourceTypeName = isSourceTypeTransaction()
      ? 'BankingTransaction'
      : 'FinanceFinancialDocument'

    const result = await bulkManageBankingReconciliations({
      variables: {
        companyId: company.id,
        reconciliations: payload,
        reconciliationIdsToRemove: reconciliationIdsToRemove,
      },
      update: (cache, { data }) => {
        if (reconciliationIdsToRemove.length) {
          reconciliationIdsToRemove.forEach((id) => {
            cache.evict({
              id: cache.identify({
                id: id,
                __typename: 'BankingReconciliation',
              }),
            })
          })
        }

        if (data?.bankingMutations.bulkManageBankingReconciliation.reconciliations?.length == 0) {
          cache.evict({
            id: cache.identify({
              id: source?.id,
              __typename: sourceTypeName,
            }),
            fieldName: 'fullyReconciledAt',
          })
        }
      },
    })

    app.setProgress(null)
    setShowPartialPendingInvoiceModal(false)
    setShowPartialPendingTransactionModal(false)
    setShowSplitAmountsModal(false)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.reconciliations.submit_messages.creating_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.reconciliations.submit_messages.creating_success'),
      })
      setDirtyForm(false)
      const createdReconciliations = handled.data.reconciliations || []
      const updatedTransaction = createdReconciliations[0]?.transaction

      setReconciliationValues([])
      setReconciliationIdsToRemove([])

      if (updatedTransaction?.fullyReconciledAt && onTransactionFullyReconciled) {
        onTransactionFullyReconciled()
      }
    }
  }

  return {
    handleReconcileClick,
    handleNoDocumentReconcileClick,
    handleNoDocumentSubmitModal,
    handleSubmitSplitAmountsModal,
    handleSubmitPartialModal,
    removeReconciliation,
    addReconciliation,
    isSelectedNewReconciliation,
    dirtyForm,
    selectedReconciliations,
    newReconciliations,
    initialAmountPendingToBeReconciled,
    firstReconciliationAmountCents: reconciliationValues[0]?.transactionAmountCents,
    firstReconciliationAmountCentsToReconcile: firstNewReconciliation?.amountCentsToReconcile,
  }
}
